import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import z from 'zod';

// @mui material components
import Card from '@mui/material/Card';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React TS imports
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import BasicLayout from 'src/md-layouts/authentication/components/BasicLayout';
import bgImage from 'src/assets/images/bg-sign-in-basic.jpeg';

import { useWorkOSCallback } from 'src/api/auth/useWorkOSCallback';
import useAuth from 'src/api/auth/useAuth';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { Route as rootRoute } from 'src/pages/__root';

export const Route = createRoute({
  path: '/auth/wos/callback',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
  beforeLoad: ({ context, search }) => {
    // Redirect if already authenticated and no code present
    if (context.auth.currentUser && !search.code) {
      throw redirect({ to: '/' });
    }
  },
  component: WorkOSCallbackPage,
});

export function WorkOSCallbackPage() {
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const { code, state, error: urlError, error_description } = Route.useSearch();
  const [hasProcessed, setHasProcessed] = useState(false);

  const {
    mutate: workOSCallbackApi,
    isLoading: isAuthenticating,
    error: callbackError,
  } = useWorkOSCallback(async () => {
    await refetchUser();
    navigate({ to: '/' });
  });

  useEffect(() => {
    if (urlError) {
      // Don't process if there's an error from WorkOS
      return;
    }

    if (code && !hasProcessed) {
      setHasProcessed(true);
      workOSCallbackApi({ code, state });
    } else if (!code && !hasProcessed) {
      // No code and no error means invalid callback
      setHasProcessed(true);
    }
  }, [code, urlError, hasProcessed, state, workOSCallbackApi]);

  const displayError = urlError
    ? new Error(error_description || urlError)
    : callbackError;

  const shouldShowError = displayError || (!code && hasProcessed);

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {isAuthenticating ? 'Completing sign in...' : 'Authentication'}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            {isAuthenticating ? (
              <CircularProgress size={40} />
            ) : shouldShowError ? (
              <MDBox width="100%">
                <MDTypography
                  variant="body2"
                  color="text"
                  textAlign="center"
                  mb={2}
                >
                  {!code && !displayError
                    ? 'Authentication failed. No authorization code received.'
                    : 'Authentication failed. Please try again.'}
                </MDTypography>
                <ErrorMessage error={displayError} mt={2} />
              </MDBox>
            ) : null}
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}
